import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { ArrowLeft, Save, Upload, Sparkles, RefreshCw, CheckCircle2, X, Check, Eye, Variable, RotateCcw, ImageIcon, ExternalLink, FileText } from "lucide-react";
import { Toaster, toast } from "sonner";
import PrivacyIcon from "../components/PrivacyIcon";
import {
  REL_TYPE_OPTIONS_EDIT as REL_TYPE_OPTIONS,
  THEIR_REL_STATUS_OPTIONS,
} from "../constants/relationshipOptions";
import { useCoinGate } from "../contexts/CoinGateContext";
import UpgradeRequiredModal from "../components/UpgradeRequiredModal";

// Relationship types with descriptions (using {match} and {user} as placeholders)
const RELATIONSHIP_TYPES = [
  { value: "stranger", label: "Stranger", description: "{match} has never met {user} before." },
  { value: "acquaintance", label: "Acquaintance", description: "{match} has met {user} before but they hardly know each other." },
  { value: "friend", label: "Friend", description: "{match} and {user} are mutual friends only." },
  { value: "best_friend", label: "Best Friend", description: "{match} and {user} are the best of friends." },
  { value: "love_interest", label: "Love Interest", description: "{match} is a romantic interest of {user}." },
  { value: "partner", label: "Partner", description: "{match} and {user} are in a relationship." },
  { value: "life_partner", label: "Life-Partner", description: "{match} and {user} are committed life partners." },
  { value: "brother", label: "Brother", description: "{match} is {user}'s brother." },
  { value: "sister", label: "Sister", description: "{match} is {user}'s sister." },
  { value: "step_brother", label: "Step-Brother", description: "{match} is {user}'s step brother." },
  { value: "step_sister", label: "Step-Sister", description: "{match} is {user}'s step sister." },
  { value: "mother", label: "Mother", description: "{match} is {user}'s mother." },
  { value: "father", label: "Father", description: "{match} is {user}'s father." },
  { value: "grandmother", label: "Grand-Mother", description: "{match} is {user}'s grand mother." },
  { value: "grandfather", label: "Grand-Father", description: "{match} is {user}'s grand father." },
  { value: "daughter", label: "Daughter", description: "{match} is {user}'s daughter." },
  { value: "son", label: "Son", description: "{match} is {user}'s son." },
  { value: "cousin", label: "Cousin", description: "{match} is {user}'s cousin." },
  { value: "work_colleague", label: "Work Colleague", description: "{match} is {user}'s work colleague." },
  { value: "teacher", label: "Teacher", description: "{match} is one of {user}'s teachers." },
  { value: "boss", label: "Boss", description: "{match} is {user}'s work boss." },
  { value: "client", label: "Client", description: "{match} is one of {user}'s clients." },
  { value: "student", label: "Student", description: "{match} is one of {user}'s students." },
];

// PersonaConnection levels (1-13)
const PERSONA_CONNECTION_LEVELS = [
  { value: 1, title: "Spark", description: "First awareness and curiosity" },
  { value: 2, title: "Opening", description: "Initial contact begins" },
  { value: 3, title: "Resonance", description: "Conversation flows naturally" },
  { value: 4, title: "Safety", description: "Trust forms through consistency" },
  { value: 5, title: "Curiosity", description: "Wanting to know more" },
  { value: 6, title: "Attraction", description: "Chemistry builds" },
  { value: 7, title: "Vulnerability", description: "Walls soften" },
  { value: 8, title: "Alignment", description: "Values line up" },
  { value: 9, title: "Momentum", description: "Connection strengthens" },
  { value: 10, title: "Intimacy", description: "Closeness deepens" },
  { value: 11, title: "Integration", description: "Weaving into daily life" },
  { value: 12, title: "Commitment", description: "Clear choice made" },
  { value: 13, title: "Union", description: "Stable mutual bond" },
];

// Relationship Type options — imported from shared constants module.
// (See /app/frontend/src/constants/relationshipOptions.js)

// Body Type options
const BODY_TYPES = [
  { value: "", label: "Select..." },
  { value: "slim", label: "Slim" },
  { value: "athletic", label: "Athletic" },
  { value: "average", label: "Average" },
  { value: "curvy", label: "Curvy" },
  { value: "muscular", label: "Muscular" },
  { value: "heavyset", label: "Heavyset" },
];

// Select/dropdown field names - these always get overwritten, not appended
const SELECT_FIELDS = [
  "relationship_status", "sex", "pronouns", "sexual_orientation", 
  "their_relationship_status", "relationship_type", 
  "primary_love_lang", "secondary_love_lang", "attachment_style", "persona_connection",
  "body_type", "stress_response", "connection_style", "communication_pref_style",
  "message_length_pref", "conflict_style", "emotional_openness", "needs_consistency",
  "relationship_feel_pref", "social_energy", "connection_rhythm", "flirting_style",
  "physical_touch_importance", "date_vibe_pref"
];

// Attachment styles with descriptions
const ATTACHMENT_STYLES = [
  { value: "secure", label: "Secure", description: "I am worthy of love, and others are generally reliable." },
  { value: "anxious_preoccupied", label: "Anxious (Preoccupied)", description: "I need closeness to feel safe, but I might be abandoned." },
  { value: "avoidant_dismissive", label: "Avoidant (Dismissive)", description: "I am safest relying on myself; closeness is risky." },
  { value: "fearful_avoidant", label: "Fearful-Avoidant (Disorganized)", description: "I want closeness, but it feels dangerous." },
  { value: "anxious_avoidant", label: "Anxious-Avoidant (Mixed)", description: "I desire connection, but it overwhelms me when it arrives." },
  { value: "disorganized", label: "Disorganized (Trauma-Linked)", description: "Love is unpredictable and unsafe." },
  { value: "earned_secure", label: "Earned Secure", description: "I can create safety and trust through awareness and repair." },
];

// Modal options for multi-select fields
const RELATIONSHIP_MATTERS_OPTIONS = [
  { category: "Emotional Foundation", options: [
    { value: "trust_honesty", label: "Trust and honesty" },
    { value: "emotional_safety", label: "Emotional safety" },
    { value: "feeling_understood", label: "Feeling understood and seen" },
    { value: "mutual_respect", label: "Mutual respect" },
  ]},
  { category: "Communication & Connection", options: [
    { value: "open_communication", label: "Open and clear communication" },
    { value: "talk_through_problems", label: "Being able to talk through problems" },
    { value: "feeling_appreciated", label: "Feeling appreciated and valued" },
  ]},
  { category: "Values & Alignment", options: [
    { value: "shared_values", label: "Shared values and principles" },
    { value: "integrity_accountability", label: "Integrity and accountability" },
    { value: "growth_awareness", label: "Growth and self-awareness" },
    { value: "shared_spiritual", label: "Shared spiritual values" },
  ]},
  { category: "Presence & Effort", options: [
    { value: "consistent_effort", label: "Consistent effort and follow-through" },
    { value: "quality_time", label: "Quality time and shared experiences" },
    { value: "reliability_stability", label: "Reliability and stability" },
  ]},
  { category: "Autonomy & Balance", options: [
    { value: "independence_space", label: "Independence and personal space" },
    { value: "support_goals", label: "Support for each other's goals" },
    { value: "healthy_boundaries", label: "Healthy boundaries" },
  ]},
];

const SHUTDOWN_TRIGGERS_OPTIONS = [
  { category: "Communication & Effort", options: [
    { value: "low_effort_replies", label: "Low effort or one-word replies" },
    { value: "talked_over", label: "Being talked over or not listened to" },
    { value: "too_many_questions", label: "Too many questions or long monologues" },
  ]},
  { category: "Emotional Safety", options: [
    { value: "feeling_judged", label: "Feeling judged or criticised" },
    { value: "feelings_dismissed", label: "My feelings being minimised or dismissed" },
  ]},
  { category: "Boundaries & Pace", options: [
    { value: "pressure_to_move", label: "Pressure to move faster than I'm comfortable" },
    { value: "sexual_escalation_early", label: "Sexual escalation too early" },
  ]},
  { category: "Trust & Consistency", options: [
    { value: "dishonesty", label: "Dishonesty or half-truths" },
    { value: "inconsistent_behavior", label: "Inconsistent behaviour or mixed signals" },
  ]},
  { category: "Presence & Energy", options: [
    { value: "overwhelming_intensity", label: "Intensity that feels overwhelming" },
    { value: "emotionally_unavailable", label: "Emotionally unavailable or avoidant behaviour" },
  ]},
  { category: "Digital Behaviour", options: [
    { value: "ghosting", label: "Ghosting or disappearing without explanation" },
    { value: "excessive_messaging", label: "Excessive or intrusive messaging" },
  ]},
  { category: "Values & Respect", options: [
    { value: "disrespectful_controlling", label: "Disrespectful or controlling behaviour" },
    { value: "lack_accountability", label: "Lack of accountability" },
  ]},
];

const ATTRACTION_QUALITIES_OPTIONS = [
  { category: "Energy & Presence", options: [
    { value: "calm_grounded", label: "Calm and grounded" },
    { value: "confident", label: "Confident (not arrogant)" },
    { value: "playful_fun", label: "Playful and fun" },
    { value: "emotionally_present", label: "Emotionally present" },
  ]},
  { category: "Emotional & Relational", options: [
    { value: "kind_considerate", label: "Kind and considerate" },
    { value: "emotionally_open", label: "Emotionally open and honest" },
    { value: "secure_themselves", label: "Secure in themselves" },
  ]},
  { category: "Communication & Mind", options: [
    { value: "clear_direct", label: "Clear and direct communicator" },
    { value: "curious_engaging", label: "Curious and engaging" },
    { value: "good_listener", label: "Good listener" },
  ]},
  { category: "Attraction Style", options: [
    { value: "warm_affectionate", label: "Warm and affectionate" },
    { value: "gently_flirtatious", label: "Gently flirtatious" },
    { value: "direct_interest", label: "Direct about their interest" },
  ]},
];

// Styled input classes
const inputClass = "mt-2 w-full px-3 py-2 bg-divine-black border border-divine-gold/30 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-divine-gold/60";
const smallInputClass = "mt-2 w-full px-3 py-2 bg-divine-black border border-divine-gold/30 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-divine-gold/60 text-sm";
const textareaClass = "mt-2 w-full px-3 py-2 bg-divine-black border border-divine-gold/30 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-divine-gold/60 min-h-[80px] resize-y";
const smallTextareaClass = "mt-2 w-full px-3 py-2 bg-divine-black border border-divine-gold/30 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-divine-gold/60 min-h-[60px] resize-y text-sm";
const selectClass = "mt-2 w-full px-3 py-2 bg-divine-black border border-divine-gold/30 rounded-md text-white focus:outline-none focus:border-divine-gold/60";
const labelClass = "text-white/80 text-sm font-medium";

// Debug field label component (used in form - privacy icon passed separately)
const FieldLabel = ({ label, fieldName, showDebug }) => (
  <label className={labelClass}>
    {label}
    {showDebug && fieldName && (
      <span className="ml-2 text-xs text-cyan-400 font-mono bg-cyan-400/10 px-1 rounded">
        {fieldName}
      </span>
    )}
  </label>
);

// Field label with privacy icon
const FieldLabelWithPrivacy = ({ 
  label, 
  fieldName, 
  showDebug, 
  showPrivacy, 
  privacyLevel, 
  matchId, 
  matchName, 
  onPrivacyChange 
}) => (
  <label className={`${labelClass} flex items-center gap-2`}>
    {showPrivacy && matchId && fieldName && (
      <PrivacyIcon
        fieldKey={fieldName}
        privacyLevel={privacyLevel || 0}
        matchId={matchId}
        matchName={matchName}
        onPrivacyChange={onPrivacyChange}
        size="sm"
      />
    )}
    {label}
    {showDebug && fieldName && (
      <span className="ml-2 text-xs text-cyan-400 font-mono bg-cyan-400/10 px-1 rounded">
        {fieldName}
      </span>
    )}
  </label>
);

// Multi-select modal component
const MultiSelectModal = ({ isOpen, onClose, title, options, selected, onSave, maxSelections = 5 }) => {
  const [localSelected, setLocalSelected] = useState([]);
  
  // Reset local state when selected prop changes and modal is open
  const selectedKey = JSON.stringify(selected || []);
  const [prevSelectedKey, setPrevSelectedKey] = useState(selectedKey);
  
  if (isOpen && selectedKey !== prevSelectedKey) {
    setLocalSelected(selected || []);
    setPrevSelectedKey(selectedKey);
  }

  const toggleOption = (value) => {
    if (localSelected.includes(value)) {
      setLocalSelected(localSelected.filter(v => v !== value));
    } else if (localSelected.length < maxSelections) {
      setLocalSelected([...localSelected, value]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-divine-charcoal border-divine-gold/30 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-divine-gold font-cinzel">{title}</DialogTitle>
          <DialogDescription className="text-white/60">
            Select up to {maxSelections} options ({localSelected.length}/{maxSelections} selected)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {options.map((category) => (
            <div key={category.category}>
              <h4 className="text-white/80 text-sm font-medium mb-2">{category.category}</h4>
              <div className="grid grid-cols-1 gap-2">
                {category.options.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      localSelected.includes(option.value)
                        ? "bg-divine-gold/20 border border-divine-gold/50"
                        : "bg-divine-black/30 border border-transparent hover:bg-white/5"
                    } ${localSelected.length >= maxSelections && !localSelected.includes(option.value) ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={localSelected.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      disabled={localSelected.length >= maxSelections && !localSelected.includes(option.value)}
                      className="accent-divine-gold"
                    />
                    <span className="text-white text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-divine-gold/30 text-divine-gold">
            Cancel
          </Button>
          <Button onClick={() => { onSave(localSelected); onClose(); }} className="bg-divine-gold text-black">
            Save Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RelationshipEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { matchId } = useParams();
  const { user } = useAuth();
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const isEditMode = !!matchId;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const { requireCoins } = useCoinGate();
  const [uploading, setUploading] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [autoPopulatingImage, setAutoPopulatingImage] = useState(false);
  const autoImageInputRef = useRef(null);
  const [applyingFields, setApplyingFields] = useState(false);
  const [extractedFields, setExtractedFields] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [appliedFields, setAppliedFields] = useState({}); // Track which fields have been successfully applied
  const [skippedFields, setSkippedFields] = useState({}); // Track fields that server skipped (existing value in "skip" mode)
  const [selectedRelationship, setSelectedRelationship] = useState(() => 
    location.state?.generatedPersona?.relationship_status || "friend"
  );
  const [currentMatchId, setCurrentMatchId] = useState(matchId || null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateMode, setUpdateMode] = useState("skip");
  const [picture, setPicture] = useState("");
  const [selectedAttachmentStyle, setSelectedAttachmentStyle] = useState(() =>
    location.state?.generatedPersona?.attachment_style || ""
  );
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  // Subscription-tier cap upgrade modal — opened when POST /matches returns
  // 402 with detail.code === "relationship_limit_reached". Shape matches
  // MatchesGallery so UpgradeRequiredModal renders identically.
  const [upgradeState, setUpgradeState] = useState(null);
  const handleTierLimit402 = (error) => {
    const d = error?.response?.data?.detail;
    if (error?.response?.status === 402 && d?.code === "relationship_limit_reached") {
      setUpgradeState({
        tier: d.tier || "FREE",
        used: d.used || 0,
        limit: d.limit ?? 0,
      });
      return true;
    }
    return false;
  };
  
  // Debug mode for admins
  const [showFieldNames, setShowFieldNames] = useState(false);
  const isAdmin = user?.role === "admin";
  
  // Privacy settings (always shown in edit mode)
  const [privacySettings, setPrivacySettings] = useState({});
  const [showResetPrivacyDialog, setShowResetPrivacyDialog] = useState(false);
  const [resettingPrivacy, setResettingPrivacy] = useState(false);
  
  // Multi-select modal states
  const [relationshipMattersModal, setRelationshipMattersModal] = useState(false);
  const [shutdownTriggersModal, setShutdownTriggersModal] = useState(false);
  const [attractionTypesModal, setAttractionTypesModal] = useState(false);
  
  // Multi-select values - initialize from generatedPersona if available
  const [relationshipMatters, setRelationshipMatters] = useState(() => {
    const val = location.state?.generatedPersona?.relationship_matters;
    return Array.isArray(val) ? val : [];
  });
  const [shutdownTriggers, setShutdownTriggers] = useState(() => {
    const val = location.state?.generatedPersona?.shutdown_triggers;
    return Array.isArray(val) ? val : [];
  });
  const [attractionTypes, setAttractionTypes] = useState(() => {
    const val = location.state?.generatedPersona?.attraction_types;
    return Array.isArray(val) ? val : [];
  });
  
  // Initial data from navigation state or empty
  const generatedPersona = location.state?.generatedPersona;
  const [initialData, setInitialData] = useState(() => ({
    name: generatedPersona?.name || "",
    relationship_status: generatedPersona?.relationship_status || generatedPersona?.their_relationship_status || "",
    persona_connection: generatedPersona?.persona_connection || 1,
    history_together: generatedPersona?.history_together || "",
    notes: generatedPersona?.notes || "",
    pasted_data: "",
    bio: generatedPersona?.bio || "",
    sex: generatedPersona?.sex || "",
    pronouns: generatedPersona?.pronouns || "",
    sexual_orientation: generatedPersona?.sexual_orientation || "",
    ethnicity: generatedPersona?.ethnicity || "",
    their_relationship_status: generatedPersona?.their_relationship_status || "",
    relationship_type: generatedPersona?.relationship_type || "",
    their_current_partner: generatedPersona?.their_current_partner || "",
    their_ex_partner_details: generatedPersona?.their_ex_partner_details || "",
    your_feelings_toward_them: generatedPersona?.your_feelings_toward_them || "",
    age: generatedPersona?.age || "",
    date_of_birth: generatedPersona?.date_of_birth || "",
    time_of_birth: generatedPersona?.time_of_birth || "",
    birth_location: generatedPersona?.birth_location || "",
    living_location: generatedPersona?.living_location || "",
    // attraction_types is now handled as multiselect array, not string
    physical_attraction_type: generatedPersona?.physical_attraction_type || "",
    fantasies_dreams: generatedPersona?.fantasies_dreams || "",
    hobbies_passions: generatedPersona?.hobbies_passions || "",
    vibe_style_presentation: generatedPersona?.vibe_style_presentation || "",
    favourite_music: generatedPersona?.favourite_music || "",
    favourite_movies_series: generatedPersona?.favourite_movies_series || "",
    favourite_alt_media_blogs: generatedPersona?.favourite_alt_media_blogs || "",
    favourite_books_audio: generatedPersona?.favourite_books_audio || "",
    work_details: generatedPersona?.work_details || "",
    work_ambition_goals: generatedPersona?.work_ambition_goals || "",
    work_history: generatedPersona?.work_history || "",
    education_history: generatedPersona?.education_history || "",
    parent_details: generatedPersona?.parent_details || "",
    sibling_details: generatedPersona?.sibling_details || "",
    children_details: generatedPersona?.children_details || "",
    family_style_type_details: generatedPersona?.family_style_type_details || "",
    pets_details: generatedPersona?.pets_details || "",
    people_admire: generatedPersona?.people_admire || "",
    people_dislike: generatedPersona?.people_dislike || "",
    boundaries: generatedPersona?.boundaries || "",
    political_views: generatedPersona?.political_views || "",
    spiritual_views: generatedPersona?.spiritual_views || "",
    primary_love_lang: generatedPersona?.primary_love_lang || "",
    secondary_love_lang: generatedPersona?.secondary_love_lang || "",
    attachment_style: generatedPersona?.attachment_style || "",
    communication_style: generatedPersona?.communication_style || "",
    texting_style: generatedPersona?.texting_style || "",
    their_feelings_toward_you: generatedPersona?.their_feelings_toward_you || "",
    // New fields - now mapped from generatedPersona
    relationship_goals: generatedPersona?.relationship_goals || "",
    seeking_looking_for: generatedPersona?.seeking_looking_for || "",
    turn_offs: generatedPersona?.turn_offs || "",
    libido_intimacy: generatedPersona?.libido_intimacy || "",
    fetishes: generatedPersona?.fetishes || "",
    height: generatedPersona?.height || "",
    weight: generatedPersona?.weight || "",
    body_type: generatedPersona?.body_type || "",
    hair_color: generatedPersona?.hair_color || "",
    eye_color: generatedPersona?.eye_color || "",
    complexion: generatedPersona?.complexion || "",
    religion: generatedPersona?.religion || "",
    friends: generatedPersona?.friends || "",
    smoking_vape: generatedPersona?.smoking_vape || "",
    drinks_alcohol: generatedPersona?.drinks_alcohol || "",
    car_drive_type: generatedPersona?.car_drive_type || "",
    travel_history: generatedPersona?.travel_history || "",
    lived_history: generatedPersona?.lived_history || "",
    skills_talents: generatedPersona?.skills_talents || "",
    good_habits_self_care: generatedPersona?.good_habits_self_care || "",
    bad_habits: generatedPersona?.bad_habits || "",
    favourite_food_drink: generatedPersona?.favourite_food_drink || "",
    fears_no_go: generatedPersona?.fears_no_go || "",
    // Core Personality Signals - now mapped from generatedPersona
    self_personality_words: generatedPersona?.self_personality_words || "",
    misunderstandings: generatedPersona?.misunderstandings || "",
    relationship_matters: generatedPersona?.relationship_matters || [],
    top_dealbreakers: generatedPersona?.top_dealbreakers || "",
    stress_response: generatedPersona?.stress_response || "",
    connection_style: generatedPersona?.connection_style || "",
    communication_pref_style: generatedPersona?.communication_pref_style || "",
    message_length_pref: generatedPersona?.message_length_pref || "",
    conflict_style: generatedPersona?.conflict_style || "",
    emotional_openness: generatedPersona?.emotional_openness || "",
    needs_consistency: generatedPersona?.needs_consistency || "",
    relationship_feel_pref: generatedPersona?.relationship_feel_pref || "",
    shutdown_triggers: generatedPersona?.shutdown_triggers || [],
    topics_avoid_early: generatedPersona?.topics_avoid_early || "",
    respect_meaning: generatedPersona?.respect_meaning || "",
    social_energy: generatedPersona?.social_energy || "",
    connection_rhythm: generatedPersona?.connection_rhythm || "",
    attraction_types: generatedPersona?.attraction_types || [],
    flirting_style: generatedPersona?.flirting_style || "",
    activities_light_up: generatedPersona?.activities_light_up || "",
    physical_touch_importance: generatedPersona?.physical_touch_importance || "",
    date_vibe_pref: generatedPersona?.date_vibe_pref || "",
    shutdown_other: generatedPersona?.shutdown_other || "",
    auto_entry_text: generatedPersona?.auto_entry_text || "",
    // Physical & Mental Health - now mapped from generatedPersona
    known_mental_health_diagnosis: generatedPersona?.known_mental_health_diagnosis || [],
    possible_mental_health_issues: generatedPersona?.possible_mental_health_issues || [],
    physical_health_issues: generatedPersona?.physical_health_issues || [],
    medications_being_taken: generatedPersona?.medications_being_taken || [],
    // Additional fields from template
    user_match_goal: generatedPersona?.user_match_goal || "",
    persona_connection: generatedPersona?.persona_connection || 1
  }));

  // Fetch existing relationship data
  useEffect(() => {
    if (isEditMode) {
      fetchRelationship();
      fetchPrivacySettings();
    } else if (generatedPersona?.attachment_style) {
      setSelectedAttachmentStyle(generatedPersona.attachment_style);
    }
  }, [matchId]);

  // Populate form when generatedPersona becomes available (handles timing issues)
  useEffect(() => {
    if (!isEditMode && generatedPersona) {
      // Update all state variables from generatedPersona
      if (generatedPersona.attachment_style) {
        setSelectedAttachmentStyle(generatedPersona.attachment_style);
      }
      if (generatedPersona.relationship_status) {
        setSelectedRelationship(generatedPersona.relationship_status);
      }
      
      // Update multiselect arrays
      if (Array.isArray(generatedPersona.relationship_matters)) {
        setRelationshipMatters(generatedPersona.relationship_matters);
      }
      if (Array.isArray(generatedPersona.shutdown_triggers)) {
        setShutdownTriggers(generatedPersona.shutdown_triggers);
      }
      if (Array.isArray(generatedPersona.attraction_types)) {
        setAttractionTypes(generatedPersona.attraction_types);
      }
      
      // Update initialData with all fields
      setInitialData(prev => ({
        ...prev,
        name: generatedPersona.name || prev.name,
        relationship_status: generatedPersona.relationship_status || generatedPersona.their_relationship_status || prev.relationship_status,
        persona_connection: generatedPersona.persona_connection || prev.persona_connection,
        history_together: generatedPersona.history_together || prev.history_together,
        notes: generatedPersona.notes || prev.notes,
        bio: generatedPersona.bio || prev.bio,
        sex: generatedPersona.sex || prev.sex,
        pronouns: generatedPersona.pronouns || prev.pronouns,
        sexual_orientation: generatedPersona.sexual_orientation || prev.sexual_orientation,
        ethnicity: generatedPersona.ethnicity || prev.ethnicity,
        their_relationship_status: generatedPersona.their_relationship_status || prev.their_relationship_status,
        relationship_type: generatedPersona.relationship_type || prev.relationship_type,
        their_current_partner: generatedPersona.their_current_partner || prev.their_current_partner,
        their_ex_partner_details: generatedPersona.their_ex_partner_details || prev.their_ex_partner_details,
        your_feelings_toward_them: generatedPersona.your_feelings_toward_them || prev.your_feelings_toward_them,
        age: generatedPersona.age || prev.age,
        date_of_birth: generatedPersona.date_of_birth || prev.date_of_birth,
        time_of_birth: generatedPersona.time_of_birth || prev.time_of_birth,
        birth_location: generatedPersona.birth_location || prev.birth_location,
        living_location: generatedPersona.living_location || prev.living_location,
        physical_attraction_type: generatedPersona.physical_attraction_type || prev.physical_attraction_type,
        fantasies_dreams: generatedPersona.fantasies_dreams || prev.fantasies_dreams,
        hobbies_passions: generatedPersona.hobbies_passions || prev.hobbies_passions,
        vibe_style_presentation: generatedPersona.vibe_style_presentation || prev.vibe_style_presentation,
        favourite_music: generatedPersona.favourite_music || prev.favourite_music,
        favourite_movies_series: generatedPersona.favourite_movies_series || prev.favourite_movies_series,
        favourite_alt_media_blogs: generatedPersona.favourite_alt_media_blogs || prev.favourite_alt_media_blogs,
        favourite_books_audio: generatedPersona.favourite_books_audio || prev.favourite_books_audio,
        work_details: generatedPersona.work_details || prev.work_details,
        work_ambition_goals: generatedPersona.work_ambition_goals || prev.work_ambition_goals,
        work_history: generatedPersona.work_history || prev.work_history,
        education_history: generatedPersona.education_history || prev.education_history,
        parent_details: generatedPersona.parent_details || prev.parent_details,
        sibling_details: generatedPersona.sibling_details || prev.sibling_details,
        children_details: generatedPersona.children_details || prev.children_details,
        family_style_type_details: generatedPersona.family_style_type_details || prev.family_style_type_details,
        pets_details: generatedPersona.pets_details || prev.pets_details,
        people_admire: generatedPersona.people_admire || prev.people_admire,
        people_dislike: generatedPersona.people_dislike || prev.people_dislike,
        boundaries: generatedPersona.boundaries || prev.boundaries,
        political_views: generatedPersona.political_views || prev.political_views,
        spiritual_views: generatedPersona.spiritual_views || prev.spiritual_views,
        primary_love_lang: generatedPersona.primary_love_lang || prev.primary_love_lang,
        secondary_love_lang: generatedPersona.secondary_love_lang || prev.secondary_love_lang,
        attachment_style: generatedPersona.attachment_style || prev.attachment_style,
        communication_style: generatedPersona.communication_style || prev.communication_style,
        texting_style: generatedPersona.texting_style || prev.texting_style,
        their_feelings_toward_you: generatedPersona.their_feelings_toward_you || prev.their_feelings_toward_you,
        relationship_goals: generatedPersona.relationship_goals || prev.relationship_goals,
        seeking_looking_for: generatedPersona.seeking_looking_for || prev.seeking_looking_for,
        turn_offs: generatedPersona.turn_offs || prev.turn_offs,
        libido_intimacy: generatedPersona.libido_intimacy || prev.libido_intimacy,
        fetishes: generatedPersona.fetishes || prev.fetishes,
        height: generatedPersona.height || prev.height,
        weight: generatedPersona.weight || prev.weight,
        body_type: generatedPersona.body_type || prev.body_type,
        hair_color: generatedPersona.hair_color || prev.hair_color,
        eye_color: generatedPersona.eye_color || prev.eye_color,
        complexion: generatedPersona.complexion || prev.complexion,
        religion: generatedPersona.religion || prev.religion,
        friends: generatedPersona.friends || prev.friends,
        smoking_vape: generatedPersona.smoking_vape || prev.smoking_vape,
        drinks_alcohol: generatedPersona.drinks_alcohol || prev.drinks_alcohol,
        car_drive_type: generatedPersona.car_drive_type || prev.car_drive_type,
        travel_history: generatedPersona.travel_history || prev.travel_history,
        lived_history: generatedPersona.lived_history || prev.lived_history,
        skills_talents: generatedPersona.skills_talents || prev.skills_talents,
        good_habits_self_care: generatedPersona.good_habits_self_care || prev.good_habits_self_care,
        bad_habits: generatedPersona.bad_habits || prev.bad_habits,
        favourite_food_drink: generatedPersona.favourite_food_drink || prev.favourite_food_drink,
        fears_no_go: generatedPersona.fears_no_go || prev.fears_no_go,
        self_personality_words: generatedPersona.self_personality_words || prev.self_personality_words,
        misunderstandings: generatedPersona.misunderstandings || prev.misunderstandings,
        top_dealbreakers: generatedPersona.top_dealbreakers || prev.top_dealbreakers,
        stress_response: generatedPersona.stress_response || prev.stress_response,
        connection_style: generatedPersona.connection_style || prev.connection_style,
        communication_pref_style: generatedPersona.communication_pref_style || prev.communication_pref_style,
        message_length_pref: generatedPersona.message_length_pref || prev.message_length_pref,
        conflict_style: generatedPersona.conflict_style || prev.conflict_style,
        emotional_openness: generatedPersona.emotional_openness || prev.emotional_openness,
        needs_consistency: generatedPersona.needs_consistency || prev.needs_consistency,
        relationship_feel_pref: generatedPersona.relationship_feel_pref || prev.relationship_feel_pref,
        topics_avoid_early: generatedPersona.topics_avoid_early || prev.topics_avoid_early,
        respect_meaning: generatedPersona.respect_meaning || prev.respect_meaning,
        social_energy: generatedPersona.social_energy || prev.social_energy,
        connection_rhythm: generatedPersona.connection_rhythm || prev.connection_rhythm,
        flirting_style: generatedPersona.flirting_style || prev.flirting_style,
        activities_light_up: generatedPersona.activities_light_up || prev.activities_light_up,
        physical_touch_importance: generatedPersona.physical_touch_importance || prev.physical_touch_importance,
        date_vibe_pref: generatedPersona.date_vibe_pref || prev.date_vibe_pref,
        shutdown_other: generatedPersona.shutdown_other || prev.shutdown_other,
        user_match_goal: generatedPersona.user_match_goal || prev.user_match_goal,
      }));
      
      // Force form inputs to update with new values
      const form = formRef.current;
      if (form) {
        Object.keys(generatedPersona).forEach(key => {
          const el = form.elements[key];
          if (el && generatedPersona[key] !== undefined && generatedPersona[key] !== null) {
            if (typeof generatedPersona[key] === 'string' || typeof generatedPersona[key] === 'number') {
              el.value = generatedPersona[key];
            }
          }
        });
      }
    }
  }, [generatedPersona, isEditMode]);

  const fetchPrivacySettings = async () => {
    try {
      const response = await authAxios.get(`/privacy-settings/${matchId}`);
      setPrivacySettings(response.data.field_privacy || {});
    } catch (error) {
      console.error("Error fetching privacy settings:", error);
    }
  };

  const handlePrivacyChange = (fieldKey, newLevel) => {
    setPrivacySettings(prev => ({
      ...prev,
      [fieldKey]: newLevel
    }));
  };

  const handleResetPrivacy = async () => {
    try {
      setResettingPrivacy(true);
      await authAxios.post(`/privacy-settings/${matchId}/reset`);
      await fetchPrivacySettings(); // Refresh privacy settings
      toast.success("Privacy levels reset to defaults");
      setShowResetPrivacyDialog(false);
    } catch (error) {
      console.error("Error resetting privacy:", error);
      toast.error("Failed to reset privacy levels");
    } finally {
      setResettingPrivacy(false);
    }
  };

  const fetchRelationship = async () => {
    try {
      const response = await authAxios.get(`/matches/${matchId}`);
      const data = response.data;
      setInitialData({
        ...initialData,
        name: data.name || "",
        match_type: data.match_type || "match_persona",
        relationship_status: data.relationship_status || "friend",
        persona_connection: data.persona_connection || 1,
        picture: data.picture || "",
        picture_url: data.picture_url || "",
        thumbnail_url: data.thumbnail_url || "",
        user_match_goal: data.user_match_goal || "",
        history_together: data.history_together || "",
        notes: data.notes || "",
        pasted_data: data.pasted_data || "",
        bio: data.bio || "",
        sex: data.sex || "",
        pronouns: data.pronouns || "",
        sexual_orientation: data.sexual_orientation || "",
        ethnicity: data.ethnicity || "",
        their_relationship_status: data.their_relationship_status || "",
        relationship_type: data.relationship_type || "",
        their_current_partner: data.their_current_partner || "",
        their_ex_partner_details: data.their_ex_partner_details || "",
        your_feelings_toward_them: data.your_feelings_toward_them || "",
        age: data.age || "",
        date_of_birth: data.date_of_birth || "",
        time_of_birth: data.time_of_birth || "",
        birth_location: data.birth_location || "",
        living_location: data.living_location || "",
        attraction_types: data.attraction_types || "",
        physical_attraction_type: data.physical_attraction_type || "",
        fantasies_dreams: data.fantasies_dreams || "",
        hobbies_passions: data.hobbies_passions || "",
        vibe_style_presentation: data.vibe_style_presentation || "",
        favourite_music: data.favourite_music || "",
        favourite_movies_series: data.favourite_movies_series || "",
        favourite_alt_media_blogs: data.favourite_alt_media_blogs || "",
        favourite_books_audio: data.favourite_books_audio || "",
        work_details: data.work_details || "",
        work_ambition_goals: data.work_ambition_goals || "",
        work_history: data.work_history || "",
        education_history: data.education_history || "",
        parent_details: data.parent_details || "",
        sibling_details: data.sibling_details || "",
        children_details: data.children_details || "",
        family_style_type_details: data.family_style_type_details || "",
        pets_details: data.pets_details || "",
        people_admire: data.people_admire || "",
        people_dislike: data.people_dislike || "",
        boundaries: data.boundaries || "",
        political_views: data.political_views || "",
        spiritual_views: data.spiritual_views || "",
        primary_love_lang: data.primary_love_lang || "",
        secondary_love_lang: data.secondary_love_lang || "",
        attachment_style: data.attachment_style || "",
        communication_style: data.communication_style || "",
        texting_style: data.texting_style || "",
        their_feelings_toward_you: data.their_feelings_toward_you || "",
        // New fields
        relationship_goals: data.relationship_goals || "",
        seeking_looking_for: data.seeking_looking_for || "",
        turn_offs: data.turn_offs || "",
        libido_intimacy: data.libido_intimacy || "",
        fetishes: data.fetishes || "",
        height: data.height || "",
        weight: data.weight || "",
        body_type: data.body_type || "",
        hair_color: data.hair_color || "",
        eye_color: data.eye_color || "",
        complexion: data.complexion || "",
        religion: data.religion || "",
        friends: data.friends || "",
        smoking_vape: data.smoking_vape || "",
        drinks_alcohol: data.drinks_alcohol || "",
        car_drive_type: data.car_drive_type || "",
        travel_history: data.travel_history || "",
        lived_history: data.lived_history || "",
        skills_talents: data.skills_talents || "",
        good_habits_self_care: data.good_habits_self_care || "",
        bad_habits: data.bad_habits || "",
        favourite_food_drink: data.favourite_food_drink || "",
        fears_no_go: data.fears_no_go || "",
        // Core Personality Signals
        self_personality_words: data.self_personality_words || "",
        misunderstandings: data.misunderstandings || "",
        top_dealbreakers: data.top_dealbreakers || "",
        stress_response: data.stress_response || "",
        connection_style: data.connection_style || "",
        communication_pref_style: data.communication_pref_style || "",
        message_length_pref: data.message_length_pref || "",
        conflict_style: data.conflict_style || "",
        emotional_openness: data.emotional_openness || "",
        needs_consistency: data.needs_consistency || "",
        relationship_feel_pref: data.relationship_feel_pref || "",
        topics_avoid_early: data.topics_avoid_early || "",
        respect_meaning: data.respect_meaning || "",
        social_energy: data.social_energy || "",
        connection_rhythm: data.connection_rhythm || "",
        flirting_style: data.flirting_style || "",
        activities_light_up: data.activities_light_up || "",
        physical_touch_importance: data.physical_touch_importance || "",
        date_vibe_pref: data.date_vibe_pref || "",
        shutdown_other: data.shutdown_other || "",
        auto_entry_text: "",
        // Physical & Mental Health
        known_mental_health_diagnosis: data.known_mental_health_diagnosis || [],
        possible_mental_health_issues: data.possible_mental_health_issues || [],
        physical_health_issues: data.physical_health_issues || [],
        medications_being_taken: data.medications_being_taken || []
      });
      setPicture(data.thumbnail_url || data.picture_url || data.picture || "");
      setSelectedRelationship(data.relationship_status || "friend");
      setSelectedAttachmentStyle(data.attachment_style || "");
      // Load multi-select arrays (ensure they are arrays, not strings)
      setRelationshipMatters(Array.isArray(data.relationship_matters) ? data.relationship_matters : []);
      setShutdownTriggers(Array.isArray(data.shutdown_triggers) ? data.shutdown_triggers : []);
      setAttractionTypes(Array.isArray(data.attraction_types) ? data.attraction_types : []);
      setLoading(false);
    } catch (error) {
      toast.error("Relationship not found");
      navigate("/relationships");
    }
  };

  // Get all form data from the form element
  const getFormData = useCallback(() => {
    const form = formRef.current;
    if (!form) return {};
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    data.picture = picture;
    data.persona_connection = parseInt(data.persona_connection) || 1;
    data.age = data.age ? parseInt(data.age) : null;
    data.physical_touch_importance = data.physical_touch_importance ? parseInt(data.physical_touch_importance) : null;
    // Add multi-select arrays
    data.relationship_matters = relationshipMatters;
    data.shutdown_triggers = shutdownTriggers;
    data.attraction_types = attractionTypes;
    // relationship_status is hidden for user_persona records (the <select>
    // is gated on !isUserPersona at the JSX layer). The MatchCreate
    // pydantic model still requires it on PUT, so fall back to the in-state
    // value or the persisted initial value to keep the payload valid.
    if (!data.relationship_status) {
      data.relationship_status =
        selectedRelationship || initialData.relationship_status || "self";
    }
    return data;
  }, [picture, relationshipMatters, shutdownTriggers, attractionTypes, selectedRelationship, initialData.relationship_status]);

  // Set a specific field value
  const setFieldValue = useCallback((name, value) => {
    const form = formRef.current;
    if (!form) return;
    const el = form.elements[name];
    if (el) el.value = value ?? "";
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    if (!isImage && !isVideo) {
      toast.error("Please select an image or video file");
      return;
    }
    
    // Size limits: 16MB max (media manager limit)
    const maxSize = 16 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File must be less than 16MB");
      return;
    }
    
    // Only handle images for avatar upload
    if (!isImage) {
      toast.error("Please select an image file for the profile photo");
      return;
    }
    
    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        // If we have a matchId, upload directly to media manager
        if (currentMatchId) {
          try {
            // Upload to media manager
            const uploadResponse = await authAxios.post(`/matches/${currentMatchId}/images`, {
              image_data: base64Data,
              caption: "Profile photo"
            });
            
            // Set as primary
            if (uploadResponse.data.image_id) {
              await authAxios.put(`/matches/${currentMatchId}/images/${uploadResponse.data.image_id}/primary`);
              
              // Fetch updated match data to get new picture_url
              const matchResponse = await authAxios.get(`/matches/${currentMatchId}`);
              const newPictureUrl = matchResponse.data.thumbnail_url || matchResponse.data.picture_url || matchResponse.data.picture;
              
              if (newPictureUrl) {
                setPicture(newPictureUrl);
                setInitialData(prev => ({
                  ...prev,
                  picture: newPictureUrl,
                  picture_url: matchResponse.data.picture_url,
                  thumbnail_url: matchResponse.data.thumbnail_url
                }));
              }
              
              toast.success("Photo uploaded and set as profile picture!");
            }
          } catch (uploadError) {
            console.error("Media manager upload failed:", uploadError);
            // Fallback: just set locally for now
            setPicture(base64Data);
            toast.success("Image loaded! Save to upload.");
          }
        } else {
          // No matchId yet (new persona), just set locally
          setPicture(base64Data);
          toast.success("Image loaded! Save to upload.");
        }
        
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to load file");
      setUploading(false);
    }
  };

  const handleAutoPopulate = async () => {
    const formData = getFormData();
    const autoText = formData.auto_entry_text;
    
    if (!autoText || autoText.trim().length < 10) {
      toast.error("Please enter some text to analyze (minimum 10 characters)");
      return;
    }
    
    if (!currentMatchId) {
      if (!formData.name?.trim()) {
        toast.error("Please enter a name before analyzing");
        return;
      }
      if (!formData.relationship_status) {
        toast.error("Please select a relationship status before analyzing");
        return;
      }
      
      setSaving(true);
      try {
        const { auto_entry_text, ...payloadData } = formData;
        const saveResponse = await authAxios.post("/matches", payloadData);
        const newMatchId = saveResponse.data.match_id;
        setCurrentMatchId(newMatchId);
        toast.success("Profile created! Now analyzing...");
        await performAutoPopulate(newMatchId, autoText, formData.name);
      } catch (error) {
        if (handleTierLimit402(error)) return;
        toast.error("Failed to save profile before analyzing");
      } finally {
        setSaving(false);
      }
    } else {
      await performAutoPopulate(currentMatchId, autoText, formData.name);
    }
  };

  const performAutoPopulate = async (matchIdToUse, text, personName) => {
    if (!(await requireCoins())) return;
    setAutoPopulating(true);
    try {
      const response = await authAxios.post(`/matches/${matchIdToUse}/auto-populate`, {
        raw_text: text,
        persona_name: personName
      });
      
      const extracted = response.data.extracted_fields || {};
      setExtractedFields(extracted);
      setAppliedFields({}); // Reset applied fields on new analysis
      setSkippedFields({}); // Reset skipped fields on new analysis
      
      const initialSelectedState = {};
      Object.keys(extracted).forEach(key => {
        initialSelectedState[key] = true;
      });
      setSelectedFields(initialSelectedState);
      
      toast.success(`Found ${Object.keys(extracted).length} fields to extract!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Analysis failed");
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleAutoPopulateFromImage = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    const MAX = 16 * 1024 * 1024;
    if (file.size > MAX) {
      toast.error("Image too large (max 16MB)");
      return;
    }

    const formData = getFormData();
    let matchIdToUse = currentMatchId;

    // If no match yet, save the profile first (same guards as text flow)
    if (!matchIdToUse) {
      if (!formData.name?.trim()) {
        toast.error("Please enter a name before analyzing");
        return;
      }
      if (!formData.relationship_status) {
        toast.error("Please select a relationship status before analyzing");
        return;
      }
      setSaving(true);
      try {
        const { auto_entry_text, ...payloadData } = formData;
        const saveResponse = await authAxios.post("/matches", payloadData);
        matchIdToUse = saveResponse.data.match_id;
        setCurrentMatchId(matchIdToUse);
        toast.success("Profile created! Now analyzing image...");
      } catch (error) {
        if (handleTierLimit402(error)) {
          setSaving(false);
          return;
        }
        toast.error("Failed to save profile before analyzing");
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    setAutoPopulatingImage(true);
    try {
      if (!(await requireCoins())) return;
      const fd = new FormData();
      fd.append("file", file);
      if (formData.name) fd.append("persona_name", formData.name);

      const response = await authAxios.post(
        `/matches/${matchIdToUse}/auto-populate/from-image`,
        fd,
        { timeout: 120000 }
      );

      const extracted = response.data.extracted_fields || {};
      setExtractedFields(extracted);
      setAppliedFields({});

      const initialSelectedState = {};
      Object.keys(extracted).forEach(key => {
        initialSelectedState[key] = true;
      });
      setSelectedFields(initialSelectedState);

      toast.success(`Found ${Object.keys(extracted).length} fields from image!`);
    } catch (error) {
      let errorMsg = error.response?.data?.detail;
      if (!errorMsg && typeof error.response?.data === "string") {
        errorMsg = error.response.data;
      }
      if (!errorMsg && error.response?.status) {
        errorMsg = `Image analysis failed (HTTP ${error.response.status})`;
      }
      toast.error(typeof errorMsg === "string" ? errorMsg : "Image analysis failed");
    } finally {
      setAutoPopulatingImage(false);
    }
  };

  const toggleFieldSelection = (fieldKey) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  const toggleAllFields = (selectAll) => {
    if (!extractedFields) return;
    const newState = {};
    Object.keys(extractedFields).forEach(key => {
      newState[key] = selectAll;
    });
    setSelectedFields(newState);
  };

  const handleApplyClick = () => {
    const selectedCount = Object.values(selectedFields).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.error("Please select at least one field to apply");
      return;
    }
    setShowUpdateModal(true);
  };

  const applySelectedFields = async () => {
    if (!extractedFields) return;
    
    setApplyingFields(true);
    setShowUpdateModal(false);
    
    const selectedData = {};
    const fieldsToApply = [];
    Object.entries(selectedFields).forEach(([key, isSelected]) => {
      if (isSelected && extractedFields[key] !== undefined) {
        selectedData[key] = extractedFields[key];
        fieldsToApply.push(key);
      }
    });
    
    selectedData.update_mode = updateMode;
    
    try {
      // Use PATCH for partial updates (only sending selected fields)
      const patchResp = await authAxios.patch(`/matches/${currentMatchId}`, selectedData);

      // Backend now always returns explicit updated_fields + skipped_fields,
      // even in the "all skipped" case. Trust both lists directly.
      const META_FIELDS = new Set(["updated_at", "zodiac_sign", "chinese_zodiac"]);
      const resp = patchResp?.data || {};
      const serverUpdated = new Set(
        Array.isArray(resp.updated_fields)
          ? resp.updated_fields.filter((k) => !META_FIELDS.has(k))
          : []
      );
      const serverSkipped = new Set(
        Array.isArray(resp.skipped_fields) ? resp.skipped_fields : []
      );

      // Partition the fields the user just submitted:
      //  - applied  = server confirmed it wrote them
      //  - skipped  = server explicitly listed them as skipped
      //  - unknown  = server didn't mention them (very old backend); treat as applied
      //               so we don't silently regress.
      const actuallyApplied = [];
      const actuallySkipped = [];
      fieldsToApply.forEach((key) => {
        if (serverSkipped.has(key)) {
          actuallySkipped.push(key);
        } else if (serverUpdated.has(key)) {
          actuallyApplied.push(key);
        } else if (!Array.isArray(resp.updated_fields) && !Array.isArray(resp.skipped_fields)) {
          // Legacy backend — fall back to optimistic apply
          actuallyApplied.push(key);
        } else {
          // Server returned lists but didn't mention this field — treat as
          // skipped rather than lying about applying it.
          actuallySkipped.push(key);
        }
      });

      // Only sync the on-form values for the fields the server ACTUALLY wrote.
      actuallyApplied.forEach((key) => {
        const value = selectedData[key];
        if (key === "relationship_matters" && Array.isArray(value)) {
          setRelationshipMatters(value);
        } else if (key === "shutdown_triggers" && Array.isArray(value)) {
          setShutdownTriggers(value);
        } else if (key === "attraction_types" && Array.isArray(value)) {
          setAttractionTypes(value);
        } else if (SELECT_FIELDS.includes(key)) {
          setFieldValue(key, value);
        } else {
          const form = formRef.current;
          const currentValue = form?.elements[key]?.value || "";
          if (updateMode === "append" && currentValue) {
            const trimmed = String(currentValue).trimEnd();
            const needsPeriod = !/[.!?…]$/.test(trimmed);
            const joined = `${trimmed}${needsPeriod ? "." : ""} ${String(value).trimStart()}`;
            setFieldValue(key, joined);
          } else {
            setFieldValue(key, value);
          }
        }
        if (key === "attachment_style") {
          setSelectedAttachmentStyle(value);
        }
      });

      // Mark applied + skipped (additive across multiple Apply clicks)
      setAppliedFields((prev) => {
        const updated = { ...prev };
        actuallyApplied.forEach((key) => {
          updated[key] = true;
        });
        return updated;
      });
      setSkippedFields((prev) => {
        const updated = { ...prev };
        actuallySkipped.forEach((key) => {
          updated[key] = true;
        });
        return updated;
      });

      // Deselect fields we resolved (applied or skipped) but keep the preview visible
      setSelectedFields((prev) => {
        const updated = { ...prev };
        [...actuallyApplied, ...actuallySkipped].forEach((key) => {
          updated[key] = false;
        });
        return updated;
      });

      if (actuallyApplied.length > 0 && actuallySkipped.length > 0) {
        toast.success(`Applied ${actuallyApplied.length}, skipped ${actuallySkipped.length} (existing values kept)`);
      } else if (actuallyApplied.length > 0) {
        toast.success(`Applied ${actuallyApplied.length} field${actuallyApplied.length === 1 ? "" : "s"}!`);
      } else if (actuallySkipped.length > 0) {
        toast.info(`Skipped ${actuallySkipped.length} field${actuallySkipped.length === 1 ? "" : "s"} (existing values kept)`);
      } else {
        toast.success("Nothing to apply");
      }
      // Keep extractedFields visible - don't clear it
    } catch (error) {
      console.error("Apply fields error:", error);
      let errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        // Pydantic 422 returns an array of {loc, msg, type} — surface the first one
        const first = errorMsg[0];
        errorMsg = first?.msg
          ? `${first.msg}${first.loc ? ` (${first.loc.join('.')})` : ''}`
          : JSON.stringify(errorMsg[0]);
      }
      if (!errorMsg && typeof error.response?.data === "string") {
        errorMsg = error.response.data;
      }
      if (!errorMsg && error.response?.status) {
        errorMsg = `Failed to apply fields (HTTP ${error.response.status})`;
      }
      toast.error(typeof errorMsg === "string" ? errorMsg : "Failed to apply fields");
    } finally {
      setApplyingFields(false);
    }
  };

  const handleSave = async () => {
    const formData = getFormData();
    if (!formData.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!(await requireCoins())) return;

    setSaving(true);
    try {
      const { auto_entry_text, ...payloadData } = formData;
      
      if (currentMatchId || isEditMode) {
        await authAxios.put(`/matches/${currentMatchId || matchId}`, payloadData);
        toast.success("Changes saved!");
        
        // Fire-and-forget: Generate Core Data Set in background
        const targetMatchId = currentMatchId || matchId;
        authAxios.post(`/data-sources/generate-core-dataset/${targetMatchId}`)
          .then(() => toast.success("Core data refreshed", { duration: 2000 }))
          .catch((err) => console.warn("Core data generation failed:", err));
      } else {
        const response = await authAxios.post("/matches", payloadData);
        setCurrentMatchId(response.data.match_id);
        toast.success("Relationship created!");
        
        // Fire-and-forget: Generate Core Data Set for new match
        authAxios.post(`/data-sources/generate-core-dataset/${response.data.match_id}`)
          .then(() => console.log("Core data generated for new match"))
          .catch((err) => console.warn("Core data generation failed:", err));
        
        navigate("/relationships");
      }
    } catch (error) {
      if (handleTierLimit402(error)) {
        setSaving(false);
        return;
      }
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Failed to save relationship");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  // For the user's own self-persona we hide a small set of relational fields
  // (persona_connection, relationship_status, history_together, and the two
  // feelings columns) — they describe a relationship between the user and
  // someone else, which is meaningless against the user themselves. Existing
  // DB values are preserved; this is a UI-only hide.
  const isUserPersona = initialData.match_type === "user_persona";

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />

      {/* Mobile-only floating SAVE button — top-right of the viewport so the
          user can save without scrolling to the bottom of the long form.
          Hidden on md+ because the desktop layout keeps the SAVE row in
          view. Fires the same handleSave used by the bottom buttons. */}
      <div className="md:hidden fixed top-3 right-3 z-40">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel shadow-lg"
          data-testid="mobile-top-save-btn"
        >
          <Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "SAVE"}
        </Button>
      </div>
      
      {/* Multi-select Modals */}
      <MultiSelectModal
        isOpen={relationshipMattersModal}
        onClose={() => setRelationshipMattersModal(false)}
        title="What matters most to me in a relationship?"
        options={RELATIONSHIP_MATTERS_OPTIONS}
        selected={relationshipMatters}
        onSave={setRelationshipMatters}
        maxSelections={5}
      />
      <MultiSelectModal
        isOpen={shutdownTriggersModal}
        onClose={() => setShutdownTriggersModal(false)}
        title="Things that shut me down quickly"
        options={SHUTDOWN_TRIGGERS_OPTIONS}
        selected={shutdownTriggers}
        onSave={setShutdownTriggers}
        maxSelections={5}
      />
      <MultiSelectModal
        isOpen={attractionTypesModal}
        onClose={() => setAttractionTypesModal(false)}
        title="Select the qualities that tend to spark attraction for you"
        options={ATTRACTION_QUALITIES_OPTIONS}
        selected={attractionTypes}
        onSave={setAttractionTypes}
        maxSelections={5}
      />

      {/* Reset Privacy Confirmation Dialog */}
      <Dialog open={showResetPrivacyDialog} onOpenChange={setShowResetPrivacyDialog}>
        <DialogContent className="bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="text-divine-gold font-cinzel flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Reset Privacy Levels
            </DialogTitle>
            <DialogDescription className="text-white/60">
              <span className="text-amber-400 font-semibold">Warning:</span> This will reset all existing privacy levels to System Default values and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowResetPrivacyDialog(false)}
              className="border-white/20 text-white/70"
              disabled={resettingPrivacy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPrivacy}
              disabled={resettingPrivacy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resettingPrivacy ? "Resetting..." : "Reset to Defaults"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Mode Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="text-divine-gold font-cinzel">How should we update existing fields?</DialogTitle>
            <DialogDescription className="text-white/60">
              Choose how to handle fields that already have data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {[
              { value: "skip", label: "Skip existing", desc: "Only fill empty fields" },
              { value: "overwrite", label: "Overwrite", desc: "Replace existing data" },
              { value: "append", label: "Append", desc: "Add to existing data" }
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  updateMode === option.value
                    ? "bg-divine-gold/20 border border-divine-gold/50"
                    : "bg-divine-black/50 border border-transparent hover:bg-white/5"
                }`}
              >
                <input
                  type="radio"
                  name="updateMode"
                  value={option.value}
                  checked={updateMode === option.value}
                  onChange={(e) => setUpdateMode(e.target.value)}
                  className="mt-1 accent-divine-gold"
                />
                <div>
                  <span className="text-white font-medium">{option.label}</span>
                  <p className="text-white/50 text-sm">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateModal(false)} className="border-divine-gold/30 text-divine-gold">
              Cancel
            </Button>
            <Button onClick={applySelectedFields} className="bg-divine-gold text-black">
              Apply Fields
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Avatar Larger View Modal */}
      <Dialog open={avatarModalOpen} onOpenChange={setAvatarModalOpen}>
        <DialogContent className="bg-divine-charcoal border-divine-gold/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-divine-gold font-cinzel">Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-4">
            {(picture || initialData.picture_url || initialData.thumbnail_url || initialData.picture) && (
              <img 
                src={picture || initialData.picture_url || initialData.thumbnail_url || initialData.picture}
                alt={`${initialData.name || 'Match'}'s profile photo`}
                className="max-w-full max-h-[60vh] rounded-lg object-contain"
              />
            )}
          </div>
          <DialogFooter className="flex justify-between">
            {currentMatchId && (
              <Button
                variant="outline"
                className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                onClick={() => {
                  setAvatarModalOpen(false);
                  navigate(`/relationships/${currentMatchId}?tab=images`);
                }}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Open Media Manager
              </Button>
            )}
            <Button
              variant="ghost"
              className="text-white/70 hover:text-white"
              onClick={() => setAvatarModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(isEditMode ? `/relationships/${matchId}` : "/relationships"))} className="text-divine-gold hover:bg-divine-gold/10">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                
                {/* Mobile: Match Avatar - Links to match profile (edit mode only) */}
                {isEditMode && initialData && (
                  <div 
                    className="md:hidden w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden border-2 border-divine-gold/30 flex-shrink-0 cursor-pointer hover:border-divine-gold/50 transition-colors"
                    onClick={() => navigate(`/relationships/${matchId}`)}
                    data-testid="header-match-avatar"
                  >
                    {(initialData.thumbnail_url || initialData.picture_url || initialData.picture) ? (
                      <img 
                        src={initialData.thumbnail_url || initialData.picture_url || initialData.picture} 
                        alt={initialData.name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <span className="text-divine-gold font-cinzel text-sm">{initialData.name?.[0]}</span>
                    )}
                  </div>
                )}
                
                <h1 className="text-xl md:text-2xl font-cinzel text-divine-gold">
                  {isEditMode ? `Edit ${initialData.name || "Relationship"}` : "Add New Relationship"}
                </h1>
              </div>
              <div className="hidden md:flex items-center gap-2">
                {/* Reset Privacy Button (Admin + Edit mode only) */}
                {isEditMode && isAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowResetPrivacyDialog(true)}
                    className="border-white/20 text-white/70 hover:text-amber-400 hover:border-amber-400/50 hover:bg-amber-400/10"
                    title="Reset all privacy levels to defaults"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    RESET
                  </Button>
                )}
                {/* Admin Debug Toggle */}
                {isAdmin && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowFieldNames(!showFieldNames)}
                    className={`${showFieldNames ? "text-cyan-400 bg-cyan-400/10" : "text-white/40"} hover:bg-cyan-400/10`}
                    title="Toggle field variable names (Admin)"
                  >
                    <Variable className="w-5 h-5" />
                  </Button>
                )}
                {/* Header Action Buttons */}
                <Button type="button" variant="outline" onClick={() => navigate(isEditMode ? `/relationships/${matchId}` : "/relationships")} className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10">
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave} disabled={saving} className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel">
                  <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "SAVE"}
                </Button>
              </div>
            </div>

            {/* Avatar Upload */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Photo</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  {/* Clickable Avatar */}
                  <div 
                    className={`w-24 h-24 rounded-full bg-divine-gold/10 border-2 border-divine-gold/30 overflow-hidden flex items-center justify-center ${(picture || initialData.thumbnail_url || initialData.picture_url || initialData.picture) ? 'cursor-pointer hover:border-divine-gold/60 transition-colors' : ''}`}
                    onClick={() => (picture || initialData.thumbnail_url || initialData.picture_url || initialData.picture) && setAvatarModalOpen(true)}
                    data-testid="avatar-preview"
                  >
                    {(picture || initialData.thumbnail_url || initialData.picture_url || initialData.picture) ? (
                      <img 
                        src={picture || initialData.thumbnail_url || initialData.picture_url || initialData.picture} 
                        alt="Avatar" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <span className="text-divine-gold/50 text-3xl">?</span>
                    )}
                  </div>
                  
                  {/* Upload & Media Manager Buttons */}
                  <div className="flex flex-col gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                    <Button 
                      type="button" 
                      onClick={() => fileInputRef.current?.click()} 
                      disabled={uploading} 
                      variant="outline" 
                      className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                      data-testid="upload-photo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload Photo"}
                    </Button>
                    
                    {currentMatchId && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-divine-gold/70 hover:text-divine-gold hover:bg-divine-gold/10"
                        onClick={() => navigate(`/relationships/${currentMatchId}?tab=images`)}
                        data-testid="media-manager-link"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Media Manager
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Basic Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Name *" fieldName="name" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.name} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="name" defaultValue={initialData.name} placeholder="Their name" className={inputClass} />
                  </div>
                  {!isUserPersona && (
                  <div>
                    <FieldLabelWithPrivacy label="Define their relationship to you *" fieldName="relationship_status" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.relationship_status} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <select 
                      name="relationship_status" 
                      defaultValue={initialData.relationship_status || "friend"} 
                      className={selectClass}
                      onChange={(e) => setSelectedRelationship(e.target.value)}
                    >
                      {RELATIONSHIP_TYPES.map(rel => (
                        <option key={rel.value} value={rel.value}>{rel.label}</option>
                      ))}
                    </select>
                    {(() => {
                      const relType = RELATIONSHIP_TYPES.find(r => r.value === (selectedRelationship || initialData.relationship_status || "friend"));
                      if (relType) {
                        const matchName = initialData.name || "They";
                        const userName = user?.name || "you";
                        const desc = relType.description.replace(/{match}/g, matchName).replace(/{user}/g, userName);
                        return <p className="mt-2 text-sm text-white/50 italic">{desc}</p>;
                      }
                      return null;
                    })()}
                  </div>
                  )}
                </div>
                {!isUserPersona && (
                <div>
                  <FieldLabelWithPrivacy label="Connection Level" fieldName="persona_connection" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.persona_connection} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <select name="persona_connection" defaultValue={initialData.persona_connection} className={selectClass}>
                    {PERSONA_CONNECTION_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>L{level.value}: {level.title} - {level.description}</option>
                    ))}
                  </select>
                </div>
                )}
                {!isUserPersona && (
                <div className="md:col-span-2">
                  <FieldLabel label="What is your target goal for this relationship?" fieldName="user_match_goal" showDebug={showFieldNames} />
                  <textarea 
                    name="user_match_goal" 
                    defaultValue={initialData.user_match_goal || ""} 
                    placeholder="e.g., Build a deeper friendship, explore romantic potential, maintain professional connection..." 
                    className={`${textareaClass} min-h-[80px]`}
                  />
                </div>
                )}
              </CardContent>
            </Card>

            {/* AUTO DATA ENTRY */}
            <Card className="bg-gradient-to-br from-divine-charcoal to-divine-black border-divine-gold/30 border-2">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-divine-gold/20"><Sparkles className="w-5 h-5 text-divine-gold" /></div>
                  <div>
                    <CardTitle className="text-divine-gold font-cinzel">AUTO DATA ENTRY</CardTitle>
                    <CardDescription className="text-white/60 mt-1">Enter text info or Upload a screenshot of bios, profiles, etc to analyze and add bulk data to this profile</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <textarea name="auto_entry_text" defaultValue="" placeholder="Paste everything you know about them here..." className={`${textareaClass} min-h-[150px]`} />
                <input
                  type="file"
                  ref={autoImageInputRef}
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                  className="hidden"
                  data-testid="auto-entry-image-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleAutoPopulateFromImage(f);
                    e.target.value = "";
                  }}
                />

                {/* Extracted Fields Preview with Checkboxes — ABOVE the analyze buttons */}
                {extractedFields && Object.keys(extractedFields).length > 0 && (
                  <div className="mt-4 p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-divine-gold font-medium">
                        Extracted Data Preview:
                        {Object.keys(appliedFields).length > 0 && (
                          <span className="ml-2 text-green-400 text-sm font-normal">
                            ({Object.keys(appliedFields).length} applied
                            {Object.keys(skippedFields).length > 0 && (
                              <span className="text-amber-300">, {Object.keys(skippedFields).length} skipped</span>
                            )})
                          </span>
                        )}
                        {Object.keys(appliedFields).length === 0 && Object.keys(skippedFields).length > 0 && (
                          <span className="ml-2 text-amber-300 text-sm font-normal">
                            ({Object.keys(skippedFields).length} skipped)
                          </span>
                        )}
                      </h4>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => toggleAllFields(true)} className="text-xs text-divine-gold hover:text-divine-gold-light">Select All</button>
                        <span className="text-white/30">|</span>
                        <button type="button" onClick={() => toggleAllFields(false)} className="text-xs text-white/50 hover:text-white">Deselect All</button>
                        <span className="text-white/30">|</span>
                        <button type="button" onClick={() => { setExtractedFields(null); setSelectedFields({}); setAppliedFields({}); setSkippedFields({}); }} className="text-xs text-red-400 hover:text-red-300">Clear</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm max-h-[300px] overflow-y-auto">
                      {Object.entries(extractedFields).map(([key, value]) => (
                        <label 
                          key={key} 
                          className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                            appliedFields[key] 
                              ? 'bg-green-900/30 border border-green-500/50' 
                              : skippedFields[key]
                                ? 'bg-amber-900/30 border border-amber-500/50'
                                : selectedFields[key] 
                                  ? 'bg-divine-gold/10 border border-divine-gold/30' 
                                  : 'bg-transparent border border-transparent hover:bg-white/5'
                          }`}
                        >
                          {appliedFields[key] ? (
                            <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                          ) : skippedFields[key] ? (
                            <X className="w-5 h-5 mt-0.5 text-amber-300 flex-shrink-0" />
                          ) : (
                            <input 
                              type="checkbox" 
                              checked={selectedFields[key] || false}
                              onChange={() => toggleFieldSelection(key)}
                              className="mt-1 accent-divine-gold"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`text-xs uppercase block ${appliedFields[key] ? 'text-green-400' : skippedFields[key] ? 'text-amber-300' : 'text-divine-gold/80'}`}>
                              {key.replace(/_/g, ' ')}
                              {appliedFields[key] && <span className="ml-1 text-green-300 normal-case">(applied)</span>}
                              {skippedFields[key] && <span className="ml-1 text-amber-200 normal-case">(skipped — existing value kept)</span>}
                            </span>
                            <span className="text-white text-sm break-words">{String(value).length > 60 ? String(value).substring(0, 60) + '...' : String(value)}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-white/40 mt-3">
                      {Object.values(selectedFields).filter(Boolean).length} of {Object.keys(extractedFields).length} fields selected
                      {Object.keys(appliedFields).length > 0 && (
                        <span className="text-green-400 ml-2">• {Object.keys(appliedFields).length} applied</span>
                      )}
                      {Object.keys(skippedFields).length > 0 && (
                        <span className="text-amber-300 ml-2">• {Object.keys(skippedFields).length} skipped</span>
                      )}
                    </p>
                  </div>
                )}

                {(Object.keys(appliedFields).length > 0 || Object.keys(skippedFields).length > 0) && (
                  <div className="p-3 rounded-md border border-amber-400/40 bg-amber-400/10" data-testid="apply-review-note">
                    <p className="text-sm text-amber-200">
                      <span className="font-semibold text-amber-300">NOTE:</span> Review the data has been added correctly to this profile then <span className="font-semibold text-amber-300">SAVE</span> to complete the profile update.
                      {Object.keys(skippedFields).length > 0 && (
                        <span className="block mt-1 text-xs text-amber-200/80">
                          {Object.keys(skippedFields).length} field{Object.keys(skippedFields).length === 1 ? " was" : "s were"} skipped because the profile already had a value in "Skip Existing" mode — change the mode or edit them manually if you want to overwrite.
                        </span>
                      )}
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 items-center">
                  <Button
                    type="button"
                    onClick={handleAutoPopulate}
                    disabled={autoPopulating || saving || autoPopulatingImage}
                    className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
                    data-testid="analyze-text-btn"
                  >
                    {autoPopulating || saving ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{saving ? "Saving..." : "Analyzing..."}</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" />{currentMatchId ? "ANALYZE TEXT" : "SAVE & ANALYZE TEXT"}</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => autoImageInputRef.current?.click()}
                    disabled={autoPopulating || saving || autoPopulatingImage}
                    className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
                    data-testid="analyze-image-btn"
                  >
                    {autoPopulatingImage ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Analyzing image...</>
                    ) : (
                      <><ImageIcon className="w-4 h-4 mr-2" />{currentMatchId ? "ANALYZE IMAGE" : "SAVE & ANALYZE IMAGE"}</>
                    )}
                  </Button>
                  {extractedFields && Object.keys(extractedFields).length > 0 && (
                    <Button type="button" onClick={handleApplyClick} disabled={applyingFields} className="bg-green-600 hover:bg-green-500 text-white" data-testid="apply-extracted-fields-btn">
                      {applyingFields ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Applying...</>) : (<><CheckCircle2 className="w-4 h-4 mr-2" />Apply {Object.values(selectedFields).filter(Boolean).length} Fields</>)}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Personal Identity */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Personal Identity</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div>
                  <FieldLabelWithPrivacy label="Sex" fieldName="sex" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.sex} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <select name="sex" defaultValue={initialData.sex} className={selectClass} data-testid="sex-select">
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="trans_male">Trans Male</option>
                    <option value="trans_female">Trans Female</option>
                  </select>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Pronouns" fieldName="pronouns" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.pronouns} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <select name="pronouns" defaultValue={initialData.pronouns} className={selectClass}>
                    <option value="">Select...</option>
                    <option value="he_him">He/Him</option>
                    <option value="she_her">She/Her</option>
                    <option value="they_them">They/Them</option>
                    <option value="custom">Other/Custom</option>
                  </select>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Sexual Orientation" fieldName="sexual_orientation" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.sexual_orientation} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <select name="sexual_orientation" defaultValue={initialData.sexual_orientation} className={selectClass}>
                    <option value="">Select...</option>
                    <option value="straight">Straight</option>
                    <option value="gay">Gay</option>
                    <option value="lesbian">Lesbian</option>
                    <option value="bisexual">Bisexual</option>
                    <option value="pansexual">Pansexual</option>
                    <option value="asexual">Asexual</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Ethnicity" fieldName="ethnicity" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.ethnicity} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="ethnicity" defaultValue={initialData.ethnicity} placeholder="Cultural background" className={inputClass} />
                </div>
              </CardContent>
            </Card>

            {/* Physical Appearance - NEW */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Physical Appearance</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Vibe / Style / Presentation" fieldName="vibe_style_presentation" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.vibe_style_presentation} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="vibe_style_presentation" defaultValue={initialData.vibe_style_presentation} placeholder="Their aesthetic, energy, personality vibe" className={smallTextareaClass} />
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Height" fieldName="height" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.height} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="height" defaultValue={initialData.height} placeholder="e.g. 5'8&quot; or 173cm" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Weight" fieldName="weight" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.weight} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="weight" defaultValue={initialData.weight} placeholder="e.g. 150lbs or 68kg" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Body Type" fieldName="body_type" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.body_type} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <select name="body_type" defaultValue={initialData.body_type} className={selectClass}>
                      {BODY_TYPES.map(bt => (
                        <option key={bt.value} value={bt.value}>{bt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Hair Color" fieldName="hair_color" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.hair_color} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="hair_color" defaultValue={initialData.hair_color} placeholder="e.g. Brown, Blonde" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Eye Color" fieldName="eye_color" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.eye_color} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="eye_color" defaultValue={initialData.eye_color} placeholder="e.g. Blue, Green" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Complexion" fieldName="complexion" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.complexion} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="complexion" defaultValue={initialData.complexion} placeholder="e.g. Fair, Olive" className={smallInputClass} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Demographics & Location */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Demographics & Location</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div>
                  <FieldLabelWithPrivacy label="Age" fieldName="age" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.age} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="age" type="number" defaultValue={initialData.age} placeholder="Age" className={inputClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Date of Birth" fieldName="date_of_birth" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.date_of_birth} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="date_of_birth" defaultValue={initialData.date_of_birth} placeholder="YYYY-MM-DD" className={inputClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Time of Birth" fieldName="time_of_birth" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.time_of_birth} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="time_of_birth" defaultValue={initialData.time_of_birth} placeholder="HH:MM" className={inputClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Birth Location" fieldName="birth_location" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.birth_location} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="birth_location" defaultValue={initialData.birth_location} placeholder="City, Country" className={inputClass} />
                </div>
                <div className="md:col-span-2">
                  <FieldLabelWithPrivacy label="Current Location" fieldName="living_location" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.living_location} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="living_location" defaultValue={initialData.living_location} placeholder="Where they live now" className={inputClass} />
                </div>
              </CardContent>
            </Card>

            {/* Relationship Data (renamed from Relationship Context) */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Relationship Data</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Their Relationship Status" fieldName="their_relationship_status" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.their_relationship_status} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    {/*
                      Controlled select (value + onChange) — was previously
                      uncontrolled (defaultValue). defaultValue is only read
                      on mount; if `initialData.their_relationship_status` is
                      populated by the async `fetchRelationship()` after the
                      form has already mounted (or if the legacy DB value
                      isn't in the option list), the select silently snaps
                      back to the placeholder. Controlled mode always
                      reflects state and reveals legacy values cleanly.
                    */}
                    <select
                      name="their_relationship_status"
                      value={initialData.their_relationship_status || ""}
                      onChange={(e) => setInitialData((prev) => ({ ...prev, their_relationship_status: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="">Select...</option>
                      {THEIR_REL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                      {/* Surface legacy DB values (e.g. older "complicated",
                          "widowed") so the user can see + clear them rather
                          than the dropdown silently snapping to "Select…". */}
                      {initialData.their_relationship_status &&
                        !THEIR_REL_STATUS_OPTIONS.find((o) => o.value === initialData.their_relationship_status) && (
                          <option value={initialData.their_relationship_status}>
                            {initialData.their_relationship_status} (legacy)
                          </option>
                        )}
                    </select>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Relationship Type" fieldName="relationship_type" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.relationship_type} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    {/* Controlled — see comment above. Descriptor reads
                        directly from initialData so no DOM-mutation needed. */}
                    <select
                      name="relationship_type"
                      value={initialData.relationship_type || ""}
                      onChange={(e) => setInitialData((prev) => ({ ...prev, relationship_type: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="">Select...</option>
                      {REL_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                      {/* Surface legacy DB values like "open", "casual" so
                          the user can see + clear them. */}
                      {initialData.relationship_type &&
                        !REL_TYPE_OPTIONS.find((o) => o.value === initialData.relationship_type) && (
                          <option value={initialData.relationship_type}>
                            {initialData.relationship_type} (legacy)
                          </option>
                        )}
                    </select>
                    <p className="text-white/50 text-xs mt-1 italic" style={{ display: initialData.relationship_type && REL_TYPE_OPTIONS.find(opt => opt.value === initialData.relationship_type)?.descriptor ? "block" : "none" }}>
                      {REL_TYPE_OPTIONS.find(opt => opt.value === initialData.relationship_type)?.descriptor || ""}
                    </p>
                  </div>
                </div>
                {!isUserPersona && (
                <div>
                  <FieldLabelWithPrivacy label="History Together" fieldName="history_together" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.history_together} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="history_together" defaultValue={initialData.history_together} placeholder="How you met, shared memories" className={textareaClass} />
                </div>
                )}
                <div>
                  <FieldLabelWithPrivacy label="Their Current Partner" fieldName="their_current_partner" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.their_current_partner} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="their_current_partner" defaultValue={initialData.their_current_partner} placeholder="Partner details if applicable" className={smallTextareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Their Ex Partners" fieldName="their_ex_partner_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.their_ex_partner_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="their_ex_partner_details" defaultValue={initialData.their_ex_partner_details} placeholder="Ex partner details" className={smallTextareaClass} />
                </div>
                {!isUserPersona && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Their Feelings Toward You" fieldName="their_feelings_toward_you" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.their_feelings_toward_you} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="their_feelings_toward_you" defaultValue={initialData.their_feelings_toward_you} placeholder="How they feel about you" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Your Feelings Toward Them" fieldName="your_feelings_toward_them" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.your_feelings_toward_them} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="your_feelings_toward_them" defaultValue={initialData.your_feelings_toward_them} placeholder="How you feel about them" className={smallTextareaClass} />
                  </div>
                </div>
                )}
                {/* Love Language & Attachment moved here */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Primary Love Language" fieldName="primary_love_lang" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.primary_love_lang} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <select name="primary_love_lang" defaultValue={initialData.primary_love_lang} className={selectClass}>
                      <option value="">Select...</option>
                      <option value="words_of_affirmation">Words of Affirmation</option>
                      <option value="acts_of_service">Acts of Service</option>
                      <option value="receiving_gifts">Receiving Gifts</option>
                      <option value="quality_time">Quality Time</option>
                      <option value="physical_touch">Physical Touch</option>
                    </select>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Secondary Love Language" fieldName="secondary_love_lang" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.secondary_love_lang} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <select name="secondary_love_lang" defaultValue={initialData.secondary_love_lang} className={selectClass}>
                      <option value="">Select...</option>
                      <option value="words_of_affirmation">Words of Affirmation</option>
                      <option value="acts_of_service">Acts of Service</option>
                      <option value="receiving_gifts">Receiving Gifts</option>
                      <option value="quality_time">Quality Time</option>
                      <option value="physical_touch">Physical Touch</option>
                    </select>
                  </div>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Attachment Style" fieldName="attachment_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.attachment_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <select 
                    name="attachment_style" 
                    value={selectedAttachmentStyle}
                    onChange={(e) => setSelectedAttachmentStyle(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select...</option>
                    {ATTACHMENT_STYLES.map((style) => (
                      <option key={style.value} value={style.value}>{style.label}</option>
                    ))}
                  </select>
                  {selectedAttachmentStyle && (
                    <p className="mt-2 text-white/60 text-sm italic">
                      {ATTACHMENT_STYLES.find(s => s.value === selectedAttachmentStyle)?.description}
                    </p>
                  )}
                </div>
                {/* New Relationship Data fields */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Relationship Goals" fieldName="relationship_goals" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.relationship_goals} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="relationship_goals" defaultValue={initialData.relationship_goals} placeholder="What they want from relationships" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Boundaries" fieldName="boundaries" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.boundaries} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="boundaries" defaultValue={initialData.boundaries} placeholder="Known boundaries and deal-breakers" className={smallTextareaClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Seeking / Looking For" fieldName="seeking_looking_for" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.seeking_looking_for} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="seeking_looking_for" defaultValue={initialData.seeking_looking_for} placeholder="What they're looking for" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Turn Offs" fieldName="turn_offs" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.turn_offs} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="turn_offs" defaultValue={initialData.turn_offs} placeholder="Things that turn them off" className={smallTextareaClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Libido / Intimacy" fieldName="libido_intimacy" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.libido_intimacy} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="libido_intimacy" defaultValue={initialData.libido_intimacy} placeholder="Intimacy preferences, libido" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Fetishes" fieldName="fetishes" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.fetishes} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="fetishes" defaultValue={initialData.fetishes} placeholder="Known fetishes or kinks" className={smallTextareaClass} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Core Personality Signals - NEW */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Core Personality Signals</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {/* Identity & Orientation */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Identity & Orientation</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="Self-described personality (1-3 words)" fieldName="self_personality_words" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.self_personality_words} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <input name="self_personality_words" defaultValue={initialData.self_personality_words} placeholder="e.g. calm, intense, playful" className={smallInputClass} />
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="What people often misunderstand about you" fieldName="misunderstandings" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.misunderstandings} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <input name="misunderstandings" defaultValue={initialData.misunderstandings} placeholder="Common misperceptions" className={smallInputClass} />
                    </div>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="What matters most in relationships (pick 3-5)" fieldName="relationship_matters" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.relationship_matters} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setRelationshipMattersModal(true)}
                      className="mt-2 w-full justify-start border-divine-gold/30 text-white hover:bg-divine-gold/10"
                    >
                      {Array.isArray(relationshipMatters) && relationshipMatters.length > 0 
                        ? `${relationshipMatters.length} selected: ${relationshipMatters.slice(0, 2).map(v => v.replace(/_/g, ' ')).join(', ')}${relationshipMatters.length > 2 ? '...' : ''}`
                        : "Click to select..."}
                    </Button>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Top 3 deal-breakers" fieldName="top_dealbreakers" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.top_dealbreakers} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="top_dealbreakers" defaultValue={initialData.top_dealbreakers} placeholder="Absolute deal-breakers in relationships" className={smallTextareaClass} />
                  </div>
                </div>

                {/* Emotional Orientation */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Emotional Orientation</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="When stressed, I tend to:" fieldName="stress_response" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.stress_response} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="stress_response" defaultValue={initialData.stress_response} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="withdraw">Withdraw</option>
                        <option value="talk_it_out">Talk it out</option>
                        <option value="distract_myself">Distract myself</option>
                        <option value="get_irritable">Get irritable</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="I feel most connected when:" fieldName="connection_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.connection_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="connection_style" defaultValue={initialData.connection_style} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="talking_deeply">Talking deeply</option>
                        <option value="spending_time">Spending time together</option>
                        <option value="physical_affection">Physical affection</option>
                        <option value="shared_activities">Shared activities</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Communication Preferences */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Communication Preferences</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="Preferred communication style" fieldName="communication_pref_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.communication_pref_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="communication_pref_style" defaultValue={initialData.communication_pref_style} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="direct">Direct</option>
                        <option value="gentle">Gentle</option>
                        <option value="playful">Playful</option>
                        <option value="reflective">Reflective</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="Message length preference" fieldName="message_length_pref" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.message_length_pref} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="message_length_pref" defaultValue={initialData.message_length_pref} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="short">Short</option>
                        <option value="medium">Medium</option>
                        <option value="long">Long</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="Conflict style" fieldName="conflict_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.conflict_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="conflict_style" defaultValue={initialData.conflict_style} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="avoidant">Avoidant</option>
                        <option value="collaborative">Collaborative</option>
                        <option value="assertive">Assertive</option>
                        <option value="emotional">Emotional</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Attachment & Pace */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Attachment & Pace</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="I open up emotionally:" fieldName="emotional_openness" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.emotional_openness} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="emotional_openness" defaultValue={initialData.emotional_openness} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="fast">Fast</option>
                        <option value="slowly">Slowly</option>
                        <option value="case_by_case">Case-by-case</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="I need consistency to feel safe:" fieldName="needs_consistency" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.needs_consistency} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="needs_consistency" defaultValue={initialData.needs_consistency} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="sometimes">Sometimes</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="I prefer relationships that feel:" fieldName="relationship_feel_pref" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.relationship_feel_pref} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="relationship_feel_pref" defaultValue={initialData.relationship_feel_pref} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="calm">Calm</option>
                        <option value="exciting">Exciting</option>
                        <option value="deep">Deep</option>
                        <option value="structured">Structured</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Boundaries & Sensitivities */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Boundaries & Sensitivities</h4>
                  <div>
                    <FieldLabelWithPrivacy label="Things that shut me down quickly (multi-select)" fieldName="shutdown_triggers" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.shutdown_triggers} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShutdownTriggersModal(true)}
                      className="mt-2 w-full justify-start border-divine-gold/30 text-white hover:bg-divine-gold/10"
                    >
                      {Array.isArray(shutdownTriggers) && shutdownTriggers.length > 0 
                        ? `${shutdownTriggers.length} selected: ${shutdownTriggers.slice(0, 2).map(v => v.replace(/_/g, ' ')).join(', ')}${shutdownTriggers.length > 2 ? '...' : ''}`
                        : "Click to select..."}
                    </Button>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Something else (shutdown)" fieldName="shutdown_other" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.shutdown_other} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="shutdown_other" defaultValue={initialData.shutdown_other} placeholder="Other things that shut me down" className={smallInputClass} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="Topics I don't like early" fieldName="topics_avoid_early" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.topics_avoid_early} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <input name="topics_avoid_early" defaultValue={initialData.topics_avoid_early} placeholder="Topics to avoid initially" className={smallInputClass} />
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="What respect looks like to me" fieldName="respect_meaning" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.respect_meaning} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <input name="respect_meaning" defaultValue={initialData.respect_meaning} placeholder="How respect should be shown" className={smallInputClass} />
                    </div>
                  </div>
                </div>

                {/* Energy & Rhythm */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Energy & Rhythm</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="Social energy" fieldName="social_energy" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.social_energy} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="social_energy" defaultValue={initialData.social_energy} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="introverted">Introverted</option>
                        <option value="ambivert">Ambivert</option>
                        <option value="extroverted">Extroverted</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="Ideal connection rhythm" fieldName="connection_rhythm" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.connection_rhythm} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="connection_rhythm" defaultValue={initialData.connection_rhythm} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="daily">Daily touchpoints</option>
                        <option value="few_times_week">A few times a week</option>
                        <option value="spacious">Spacious</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Attraction & Desire */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Attraction & Desire</h4>
                  <div>
                    <FieldLabelWithPrivacy label="I feel attraction when someone is: (pick up to 5)" fieldName="attraction_types" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.attraction_types} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAttractionTypesModal(true)}
                      className="mt-2 w-full justify-start border-divine-gold/30 text-white hover:bg-divine-gold/10"
                    >
                      {Array.isArray(attractionTypes) && attractionTypes.length > 0 
                        ? `${attractionTypes.length} selected: ${attractionTypes.slice(0, 2).map(v => v.replace(/_/g, ' ')).join(', ')}${attractionTypes.length > 2 ? '...' : ''}`
                        : "Click to select..."}
                    </Button>
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Flirting style" fieldName="flirting_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.flirting_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <select name="flirting_style" defaultValue={initialData.flirting_style} className={selectClass}>
                      <option value="">Select...</option>
                      <option value="playful">Playful</option>
                      <option value="direct">Direct</option>
                      <option value="subtle">Subtle</option>
                      <option value="expressive">Expressive</option>
                    </select>
                  </div>
                </div>

                {/* Sensory & Lifestyle */}
                <div className="space-y-4">
                  <h4 className="text-white/80 font-medium border-b border-divine-gold/20 pb-2">Sensory & Lifestyle</h4>
                  <div>
                    <FieldLabelWithPrivacy label="Activities that light me up" fieldName="activities_light_up" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.activities_light_up} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="activities_light_up" defaultValue={initialData.activities_light_up} placeholder="Activities that energize and excite" className={smallTextareaClass} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabelWithPrivacy label="How important is physical touch? (1-10)" fieldName="physical_touch_importance" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.physical_touch_importance} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <input name="physical_touch_importance" type="number" min="1" max="10" defaultValue={initialData.physical_touch_importance} placeholder="1-10" className={smallInputClass} />
                    </div>
                    <div>
                      <FieldLabelWithPrivacy label="Preferred date vibe" fieldName="date_vibe_pref" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.date_vibe_pref} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                      <select name="date_vibe_pref" defaultValue={initialData.date_vibe_pref} className={selectClass}>
                        <option value="">Select...</option>
                        <option value="cozy">Cozy</option>
                        <option value="adventurous">Adventurous</option>
                        <option value="intellectual">Intellectual</option>
                        <option value="sensual">Sensual</option>
                      </select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Work & Career */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Work & Career</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Current Work" fieldName="work_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.work_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="work_details" defaultValue={initialData.work_details} placeholder="Current job, company, role" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Work Ambitions & Goals" fieldName="work_ambition_goals" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.work_ambition_goals} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="work_ambition_goals" defaultValue={initialData.work_ambition_goals} placeholder="Career goals and aspirations" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Work History" fieldName="work_history" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.work_history} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="work_history" defaultValue={initialData.work_history} placeholder="Previous jobs and career path" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Education History" fieldName="education_history" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.education_history} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input type="text" name="education_history" defaultValue={initialData.education_history} placeholder="Schools, universities, degrees" className={inputClass} />
                </div>
              </CardContent>
            </Card>

            {/* Lifestyle & Interests */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Lifestyle & Interests</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Hobbies & Passions" fieldName="hobbies_passions" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.hobbies_passions} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="hobbies_passions" defaultValue={initialData.hobbies_passions} placeholder="What they love doing" className={textareaClass} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Favourite Music" fieldName="favourite_music" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.favourite_music} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="favourite_music" defaultValue={initialData.favourite_music} placeholder="Artists, bands, genres, songs they love" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Favourite Movies/Series" fieldName="favourite_movies_series" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.favourite_movies_series} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="favourite_movies_series" defaultValue={initialData.favourite_movies_series} placeholder="Movies, TV shows, series they love" className={smallTextareaClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Favourite Alt Media/Blogs" fieldName="favourite_alt_media_blogs" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.favourite_alt_media_blogs} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="favourite_alt_media_blogs" defaultValue={initialData.favourite_alt_media_blogs} placeholder="Podcasts, blogs, YouTube channels" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Favourite Books/Audio" fieldName="favourite_books_audio" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.favourite_books_audio} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="favourite_books_audio" defaultValue={initialData.favourite_books_audio} placeholder="Books, audiobooks, authors" className={smallTextareaClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Smoking / Vape" fieldName="smoking_vape" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.smoking_vape} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="smoking_vape" defaultValue={initialData.smoking_vape} placeholder="Smoking habits" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Drinks Alcohol" fieldName="drinks_alcohol" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.drinks_alcohol} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="drinks_alcohol" defaultValue={initialData.drinks_alcohol} placeholder="Drinking habits" className={smallInputClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Car / Drive Type" fieldName="car_drive_type" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.car_drive_type} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="car_drive_type" defaultValue={initialData.car_drive_type} placeholder="Vehicle, driving style" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Travel History" fieldName="travel_history" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.travel_history} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="travel_history" defaultValue={initialData.travel_history} placeholder="Places traveled" className={smallInputClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Lived History" fieldName="lived_history" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.lived_history} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="lived_history" defaultValue={initialData.lived_history} placeholder="Places lived" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Skills & Talents" fieldName="skills_talents" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.skills_talents} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="skills_talents" defaultValue={initialData.skills_talents} placeholder="Special skills or talents" className={smallInputClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Good Habits / Self-Care" fieldName="good_habits_self_care" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.good_habits_self_care} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="good_habits_self_care" defaultValue={initialData.good_habits_self_care} placeholder="Positive habits" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Bad Habits" fieldName="bad_habits" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.bad_habits} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="bad_habits" defaultValue={initialData.bad_habits} placeholder="Bad habits or vices" className={smallInputClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Favourite Food & Drink" fieldName="favourite_food_drink" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.favourite_food_drink} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="favourite_food_drink" defaultValue={initialData.favourite_food_drink} placeholder="Favorite foods and drinks" className={smallInputClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Fears / No-Go" fieldName="fears_no_go" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.fears_no_go} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <input name="fears_no_go" defaultValue={initialData.fears_no_go} placeholder="Fears or things to avoid" className={smallInputClass} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Family */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Family</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Parents/Grandparents" fieldName="parent_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.parent_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="parent_details" defaultValue={initialData.parent_details} placeholder="Parents, grandparents info" className={textareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Siblings" fieldName="sibling_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.sibling_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="sibling_details" defaultValue={initialData.sibling_details} placeholder="Siblings info" className={textareaClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Children" fieldName="children_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.children_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="children_details" defaultValue={initialData.children_details} placeholder="Children info if any" className={textareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Pets" fieldName="pets_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.pets_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="pets_details" defaultValue={initialData.pets_details} placeholder="Pets they have" className={textareaClass} />
                  </div>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Family Style / Type" fieldName="family_style_type_details" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.family_style_type_details} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="family_style_type_details" defaultValue={initialData.family_style_type_details} placeholder="Family dynamics, closeness" className={textareaClass} />
                </div>
              </CardContent>
            </Card>

            {/* Attractions & Dreams */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Attractions & Dreams</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Physically attracted to" fieldName="physical_attraction_type" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.physical_attraction_type} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="physical_attraction_type" defaultValue={initialData.physical_attraction_type} placeholder="Physical traits, body types, features they find attractive" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Fantasies & Dreams" fieldName="fantasies_dreams" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.fantasies_dreams} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="fantasies_dreams" defaultValue={initialData.fantasies_dreams} placeholder="Life dreams, relationship goals" className={textareaClass} />
                </div>
              </CardContent>
            </Card>

            {/* Social Dynamics */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Social Dynamics</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Friends" fieldName="friends" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.friends} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="friends" defaultValue={initialData.friends} placeholder="Friend groups, social circles" className={smallTextareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="People They Admire" fieldName="people_admire" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.people_admire} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="people_admire" defaultValue={initialData.people_admire} placeholder="People and traits they admire" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="People They Dislike" fieldName="people_dislike" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.people_dislike} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="people_dislike" defaultValue={initialData.people_dislike} placeholder="Types of people they avoid" className={textareaClass} />
                </div>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Values</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Political Views" fieldName="political_views" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.political_views} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="political_views" defaultValue={initialData.political_views} placeholder="Political stance" className={smallTextareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Spiritual Views" fieldName="spiritual_views" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.spiritual_views} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="spiritual_views" defaultValue={initialData.spiritual_views} placeholder="Spiritual or religious beliefs" className={smallTextareaClass} />
                  </div>
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Religion" fieldName="religion" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.religion} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <input name="religion" defaultValue={initialData.religion} placeholder="Religious affiliation" className={smallInputClass} />
                </div>
              </CardContent>
            </Card>

            {/* Communication */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Communication Style</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabelWithPrivacy label="Communication Style" fieldName="communication_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.communication_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="communication_style" defaultValue={initialData.communication_style} placeholder="How they communicate" className={textareaClass} />
                  </div>
                  <div>
                    <FieldLabelWithPrivacy label="Texting Style" fieldName="texting_style" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.texting_style} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                    <textarea name="texting_style" defaultValue={initialData.texting_style} placeholder="How they text" className={textareaClass} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Physical & Mental Health */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Physical & Mental Health</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Known Mental Health Diagnosis" fieldName="known_mental_health_diagnosis" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.known_mental_health_diagnosis} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="known_mental_health_diagnosis" defaultValue={Array.isArray(initialData.known_mental_health_diagnosis) ? initialData.known_mental_health_diagnosis.join(", ") : initialData.known_mental_health_diagnosis} placeholder="e.g. ADHD, Depression, Anxiety" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Possible Mental Health Issues" fieldName="possible_mental_health_issues" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.possible_mental_health_issues} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="possible_mental_health_issues" defaultValue={Array.isArray(initialData.possible_mental_health_issues) ? initialData.possible_mental_health_issues.join(", ") : initialData.possible_mental_health_issues} placeholder="Suspected but undiagnosed issues" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Physical Health Issues" fieldName="physical_health_issues" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.physical_health_issues} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="physical_health_issues" defaultValue={Array.isArray(initialData.physical_health_issues) ? initialData.physical_health_issues.join(", ") : initialData.physical_health_issues} placeholder="Chronic conditions, injuries, allergies" className={textareaClass} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Medications Being Taken" fieldName="medications_being_taken" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.medications_being_taken} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="medications_being_taken" defaultValue={Array.isArray(initialData.medications_being_taken) ? initialData.medications_being_taken.join(", ") : initialData.medications_being_taken} placeholder="Current medications or supplements" className={textareaClass} />
                </div>
              </CardContent>
            </Card>

            {/* Bio & Notes */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader><CardTitle className="text-divine-gold font-cinzel">Bio & Online Presence</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <FieldLabelWithPrivacy label="Bio" fieldName="bio" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.bio} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="bio" defaultValue={initialData.bio} placeholder="Short bio or about them" className={`${textareaClass} min-h-[100px]`} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Notes" fieldName="notes" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.notes} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="notes" defaultValue={initialData.notes} placeholder="Your personal notes about them" className={`${textareaClass} min-h-[100px]`} />
                </div>
                <div>
                  <FieldLabelWithPrivacy label="Raw Pasted Data" fieldName="pasted_data" showDebug={showFieldNames} showPrivacy={isEditMode} privacyLevel={privacySettings?.pasted_data} matchId={matchId} matchName={initialData.name} onPrivacyChange={handlePrivacyChange} />
                  <textarea name="pasted_data" defaultValue={initialData.pasted_data} placeholder="Any raw data you've copied" className={textareaClass} />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pb-8">
              <Button type="button" variant="outline" onClick={() => navigate("/relationships")} className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10">Cancel</Button>
              <Button type="button" onClick={handleSave} disabled={saving} className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel">
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "SAVE"}
              </Button>
            </div>
          </div>
        </form>
      </main>

      {/* Subscription-tier upgrade modal — opened when POST /matches 402s
          with code=relationship_limit_reached. Same modal as MatchesGallery. */}
      <UpgradeRequiredModal
        state={upgradeState}
        onClose={() => setUpgradeState(null)}
      />

      {/* P3 NOTE: Backend enum handling for personality trait checkboxes needed for future validation */}
    </div>
  );
};

export default RelationshipEditor;
